import { Mic, Keyboard } from 'lucide-react';
import { UI_TEXT } from '../../config/ui-text';
import { useVoiceCapture, type VoiceOrTextTranscribeContext } from './useVoiceCapture';
import { MicButtonVisual } from './MicButtonVisual';

export type { VoiceOrTextTranscribeContext } from './useVoiceCapture';

const MSG = UI_TEXT.HUMAN_SURVEY.SESSION.MESSAGES;

export interface VoiceOrTextInputProps {
  /** Current answer text. The caller coerces to a string (a non-string prior answer is never a
   *  real answer to THIS slot - see `appendTranscript`'s docblock). */
  value: string;
  onChange: (value: string) => void;

  /** Which mode is showing right now. CONTROLLED, not owned internally: the caller keeps this so
   *  it survives this component being unmounted and remounted when the surrounding question
   *  layout changes shape (e.g. a follow-up's JSX tree vs. an ordinary question's), and so it can
   *  read the mode back for its own bookkeeping (SurveySession's `input_mode` on submit). */
  showTextInput: boolean;
  onShowTextInputChange: (showTextInput: boolean) => void;

  /** Sticky, one-way "voice is broken" kill switch. Controlled for the same reason as
   *  `showTextInput` — see frontend#659: a late transcription failure must only ever ADD this
   *  restriction, never removed once set, and the caller decides its scope (session-wide in
   *  SurveySession; scoped to one dynamic section in AiDynamicSectionRunner). */
  transcriptionUnavailable: boolean;
  onTranscriptionUnavailable: () => void;

  /** Author overrides for this slot. Never both true - the author-authoring UI (QuestionCard)
   *  already prevents that, so this component does not re-validate it. */
  disableVoiceInput?: boolean;
  disableTextInput?: boolean;

  /** Identifies the answer this capture belongs to (mirrors `answerSlotKey`). A recording or an
   *  in-flight transcription started under a PREVIOUS slotKey is discarded rather than written
   *  into whatever is on screen when it resolves. */
  slotKey: string;

  transcribeContext?: VoiceOrTextTranscribeContext;

  placeholder?: string;

  /** Bubbles live recording state up. SurveySession feeds this into the idle-timeout heartbeat,
   *  which must not treat three minutes of talking as three minutes of absence. */
  onRecordingChange?: (isRecording: boolean) => void;

  /** Bubbles a user-facing message up. The caller decides where to show it - SurveySession routes
   *  it into its own top-of-page error banner; AiDynamicSectionRunner shows it inline, since a
   *  dynamic section has no such banner and must never look like a hard error (spec-01: no
   *  stop_reason is ever shown, but an ordinary "type instead" nudge is not one). */
  onError?: (message: string) => void;
}

/**
 * The voice-or-type input every question type in this survey renders, extracted so
 * `AiDynamicSectionRunner`'s emitted `open_text` turns get the exact same experience instead of a
 * bare textarea (task: "ai dynamic questioning shall work as other questions work in survey").
 *
 * The mic capture + transcription lifecycle itself lives in `useVoiceCapture` — this file is the
 * presentational half: which of the two bodies (mic vs. textarea) to paint, and the small amount
 * of channel-selection logic that is genuinely about DISPLAY (author flags disabling a mode).
 *
 * What is deliberately NOT owned here: `showTextInput` and `transcriptionUnavailable` stay
 * controlled by the caller (see their docblocks above) rather than becoming local state, because
 * this component can be unmounted and remounted as the surrounding layout changes shape (a
 * question's own body vs. its AI follow-up's) and local state would not survive that.
 */
export function VoiceOrTextInput({
  value,
  onChange,
  showTextInput,
  onShowTextInputChange,
  transcriptionUnavailable,
  onTranscriptionUnavailable,
  disableVoiceInput = false,
  disableTextInput = false,
  slotKey,
  transcribeContext,
  placeholder = UI_TEXT.HUMAN_SURVEY.SESSION.PLACEHOLDERS.OPEN_ENDED,
  onRecordingChange,
  onError,
}: VoiceOrTextInputProps) {
  const { isRecording, isTranscribing, micStatus, audioLevels, startRecording, stopRecording } =
    useVoiceCapture({
      value,
      onChange,
      onShowTextInputChange,
      onTranscriptionUnavailable,
      slotKey,
      transcribeContext,
      onRecordingChange,
      onError,
    });

  // Also true once transcription has failed - the mic is pointless if nothing can transcribe it.
  const voiceDisabled = disableVoiceInput || transcriptionUnavailable;
  const textDisabled = disableTextInput;

  if (!showTextInput) {
    return (
      <div className="text-center space-y-6">
        <div className="flex flex-col items-center space-y-4">
          <MicButtonVisual
            isRecording={isRecording}
            isTranscribing={isTranscribing}
            audioLevels={audioLevels}
            onToggle={micStatus !== 'idle' ? stopRecording : startRecording}
          />

          <div className="text-center">
            <p className="text-lg font-medium text-gray-900 mb-2">
              {micStatus === 'warming'
                ? MSG.MIC_WARMING
                : micStatus === 'listening'
                ? MSG.LISTENING
                : isTranscribing
                ? MSG.CONVERTING_SPEECH
                : MSG.TAP_TO_SPEAK}
            </p>
            {micStatus === 'idle' && !isTranscribing && (
              <p className="text-sm text-gray-500">{MSG.VOICE_CONVERTED}</p>
            )}
            {(isRecording || isTranscribing) && (
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                {isRecording && (
                  <>
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                    <span>{MSG.RECORDING_TAP_AGAIN}</span>
                  </>
                )}
                {isTranscribing && (
                  <>
                    <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    <span>{MSG.PROCESSING_SPEECH}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {value && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">{MSG.YOUR_RESPONSE}</p>
            <p className="text-gray-900">{value}</p>
          </div>
        )}

        {!textDisabled && (
          <button
            type="button"
            onClick={() => onShowTextInputChange(true)}
            className="inline-flex items-center space-x-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mt-4"
          >
            <Keyboard className="w-3 h-3" />
            <span>{MSG.SWITCH_TO_TEXT}</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">{MSG.TYPE_YOUR_ANSWER}</h3>
        {!voiceDisabled && (
          <button
            type="button"
            onClick={() => onShowTextInputChange(false)}
            className="inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
          >
            <Mic className="w-4 h-4" />
            <span>{MSG.SWITCH_TO_VOICE}</span>
          </button>
        )}
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        rows={4}
        autoFocus
      />
    </div>
  );
}
